/**
 * Reservas — a camada de API do painel para a Fase 4.
 *
 * ---------------------------------------------------------------------------
 * A MÁQUINA MORA NO SHARED; A TELA SÓ PERGUNTA
 * ---------------------------------------------------------------------------
 * Quais botões aparecem no detalhe vem de `transicoesPossiveis(status,
 * 'EMPRESA')` — a MESMA lista fechada que a API valida. A tela nunca decide se
 * uma transição existe: ela desenha o que a máquina declara, e quando o painel
 * de outro operador estiver desatualizado, a API responde 422 com o estado
 * atual nos detalhes e a query é invalidada.
 *
 * O mesmo princípio do preço vale para o FECHAMENTO: nada aqui soma linha. A
 * simulação devolve a conta pronta do motor e a tela a formata.
 *
 * ---------------------------------------------------------------------------
 * IDEMPOTÊNCIA NA CRIAÇÃO
 * ---------------------------------------------------------------------------
 * `POST /reservas` exige `Idempotency-Key` (invariante nº 9). A chave nasce no
 * momento em que o DIALOG abre — não no clique: dois cliques no mesmo
 * formulário são a mesma intenção e devolvem a mesma reserva.
 */

import type {
  AlocarVeiculoEntrada,
  AvaliarDocumento,
  CriarReservaEntrada,
  FecharReservaEntrada,
  ProrrogarReservaEntrada,
  SolicitarDocumentoEntrada,
} from '@locaveri/shared'
import {
  ATORES_DE_TRANSICAO,
  CATEGORIAS_DE_CNH,
  FATORES_DE_ALOCACAO,
  MOTIVOS_DE_BLOQUEIO_DE_ALOCACAO,
  PROVEDORES_DE_ASSINATURA,
  STATUS_DE_CONTRATO,
  STATUS_DE_DOCUMENTO,
  STATUS_DE_MULTA,
  STATUS_DE_RESERVA,
  STATUS_DE_SINISTRO,
  TIPOS_DE_DOCUMENTO_SOLICITAVEL,
  TIPOS_DE_LINHA_DE_FECHAMENTO,
  TIPOS_DE_SINISTRO,
  analiseCadastralSchema,
} from '@locaveri/shared'
import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { z } from 'zod'

import { apiGet, apiPost } from '@/lib/api'
import {
  ROTAS_DE_ALOCACOES,
  ROTAS_DE_ARQUIVOS,
  ROTAS_DE_CONTRATOS,
  ROTAS_DE_DOCUMENTOS,
  ROTAS_DE_MULTAS,
  ROTAS_DE_RESERVAS,
  ROTAS_DE_SINISTROS,
} from '@/lib/endpoints'
import { montarQuery, type ValorDeQuery } from '@/lib/query-string'
import { paginaDe } from '@/features/precos/api'

// ---------------------------------------------------------------------------
// Modelos de leitura (espelham os DTOs de saída da API)
// ---------------------------------------------------------------------------

export const statusDaReservaSchema = z.enum(STATUS_DE_RESERVA)

export const reservaResumoSchema = z.object({
  id: z.uuid(),
  numero: z.number().int(),
  status: statusDaReservaSchema,
  conta: z.object({ id: z.uuid(), nome: z.string() }),
  categoria: z.object({ id: z.uuid(), nome: z.string() }),
  retirada: z.string(),
  devolucao: z.string(),
  diarias: z.number().int(),
  total: z.string(),
  caucao: z.string(),
  veiculoAlocado: z.object({ id: z.uuid(), placa: z.string(), modelo: z.string() }).nullable(),
  criadoEm: z.string(),
})

export type ReservaResumo = z.infer<typeof reservaResumoSchema>

const condutorSchema = z.object({
  id: z.uuid(),
  nome: z.string(),
  cpf: z.string(),
  telefone: z.string().nullable(),
  cnhNumero: z.string(),
  cnhCategoria: z.enum(CATEGORIAS_DE_CNH),
  cnhValidade: z.string(),
  principal: z.boolean(),
})

export type CondutorLido = z.infer<typeof condutorSchema>

const passoDaTimelineSchema = z.object({
  status: statusDaReservaSchema,
  statusAnterior: statusDaReservaSchema.nullable(),
  acao: z.string(),
  ator: z.enum(ATORES_DE_TRANSICAO),
  motivo: z.string().nullable(),
  autor: z.object({ id: z.uuid(), nome: z.string() }).nullable(),
  criadoEm: z.string(),
})

export type PassoDaTimeline = z.infer<typeof passoDaTimelineSchema>

const alocacaoLidaSchema = z.object({
  id: z.uuid(),
  veiculo: z.object({ id: z.uuid(), placa: z.string(), modelo: z.string() }),
  inicio: z.string(),
  fim: z.string(),
  ativa: z.boolean(),
  automatica: z.boolean(),
  score: z.number().int().nullable(),
  justificativa: z.string().nullable(),
  desfeitaEm: z.string().nullable(),
  motivoDoDesfazimento: z.string().nullable(),
})

export const fechamentoSchema = z.object({
  linhas: z.array(
    z.object({
      tipo: z.enum(TIPOS_DE_LINHA_DE_FECHAMENTO),
      descricao: z.string(),
      valor: z.string(),
    }),
  ),
  totalDeCobrancas: z.string(),
  caucao: z.string(),
  caucaoRetida: z.string(),
  caucaoALiberar: z.string(),
  saldoACobrar: z.string(),
  diariasReais: z.number().int(),
  diariasDeAtraso: z.number().int(),
  kmRodado: z.number().int(),
  kmExcedente: z.number().int(),
  oitavosFaltantes: z.number().int(),

  /**
   * Crédito BRUTO da devolução antecipada (política congelada na reserva). A
   * parte que coube nas cobranças virou linha negativa; o excedente espera o
   * estorno de pagamento da Fase 5. "0" quando não há.
   */
  creditoDeDevolucaoAntecipada: z.string(),

  /**
   * A conferência do rastreador (Gap 5): o km cobrado é o da vistoria; o
   * rastreador só confere. `null` sem rastreador/offset/posição.
   */
  telemetria: z
    .object({
      kmDoRastreador: z.number().int(),
      registradoEm: z.string(),
      divergenciaKm: z.number().int(),
      alerta: z.boolean(),
    })
    .nullable(),
})

export type FechamentoLido = z.infer<typeof fechamentoSchema>

const riscoSchema = z.object({
  score: z.number().int(),
  nivel: z.string(),
  contribuicoes: z.array(z.object({ fator: z.string(), pontos: z.number().int() })),
  alertas: z.array(z.string()),
  /** O que os provedores externos disseram — snapshots antigos não trazem. */
  verificacoesExternas: z
    .array(z.object({ verificacao: z.string(), resultado: z.string() }))
    .optional(),
})

export type RiscoLido = z.infer<typeof riscoSchema>

// --- Documentos da reserva (Gap 2) -----------------------------------------

const documentoDaFilaSchema = z.object({
  id: z.uuid(),
  tipo: z.enum(TIPOS_DE_DOCUMENTO_SOLICITAVEL),
  status: z.enum(STATUS_DE_DOCUMENTO),
  instrucoes: z.string(),
  observacao: z.string().nullable(),
  enviadoEm: z.string().nullable(),
  avaliadoEm: z.string().nullable(),
})

export const documentoDaReservaSchema = documentoDaFilaSchema.extend({
  arquivoChave: z.string().nullable(),
  criadoEm: z.string(),
})

export type DocumentoDaReserva = z.infer<typeof documentoDaReservaSchema>

const listaDeDocumentosSchema = z.object({ itens: z.array(documentoDaReservaSchema) })

export const reservaDetalheSchema = reservaResumoSchema.extend({
  quoteId: z.uuid(),
  retiradaLocal: z.object({ id: z.uuid(), nome: z.string() }).nullable(),
  devolucaoLocal: z.object({ id: z.uuid(), nome: z.string() }).nullable(),
  fusoDaRetirada: z.string(),
  diariaBase: z.string(),
  toleranciaEmMinutos: z.number().int(),
  kmDiarioIncluso: z.number().int(),
  valorDoKmExcedente: z.string(),
  valorDoTanqueCheio: z.string(),
  temProtecao: z.boolean(),
  franquiaDaProtecao: z.string().nullable(),
  risco: riscoSchema.nullable(),
  expiraEm: z.string().nullable(),
  aprovadaEm: z.string().nullable(),
  observacoes: z.string().nullable(),
  condutores: z.array(condutorSchema),
  timeline: z.array(passoDaTimelineSchema),
  alocacoes: z.array(alocacaoLidaSchema),
  fechamento: fechamentoSchema.nullable(),
})

export type ReservaDetalhe = z.infer<typeof reservaDetalheSchema>

const itemDaFilaSchema = z.object({
  reserva: reservaResumoSchema,
  conta: z.object({
    id: z.uuid(),
    nome: z.string(),
    tipo: z.enum(['PF', 'PJ']),
    documento: z.string().nullable(),
    clienteDesde: z.string(),
    reservasFinalizadas: z.number().int(),
    noShows: z.number().int(),
  }),
  condutorPrincipal: condutorSchema.nullable(),
  risco: riscoSchema.nullable(),
  /** A análise do Mira Score que vale para a conta — `null` se nunca houve. */
  analiseCadastral: analiseCadastralSchema.nullable(),
  documentos: z.array(documentoDaFilaSchema),
  margemEstimada: z.string().nullable(),
  disponibilidadeReal: z.number().int().nullable(),
  /** Reservas ocupando a janela ALÉM do que a política permitia. Ver a fila. */
  excedenteDaJanela: z.number().int().nullable(),
  horasNaFila: z.number(),
})

export type ItemDaFila = z.infer<typeof itemDaFilaSchema>

const filaSchema = z.object({ itens: z.array(itemDaFilaSchema) })

const listaDeReservasSchema = paginaDe(reservaResumoSchema)
export type ListaDeReservas = z.infer<typeof listaDeReservasSchema>

// --- Candidatos e alocação --------------------------------------------------

const contribuicaoSchema = z.object({
  fator: z.enum(FATORES_DE_ALOCACAO),
  peso: z.number().int(),
  nota: z.number().int(),
  pontos: z.number().int(),
  valorObservado: z.number(),
})

const candidatoSchema = z.object({
  vehicleId: z.uuid(),
  placa: z.string(),
  modelo: z.string(),
  score: z.number().int(),
  elegivel: z.boolean(),
  contribuicoes: z.array(contribuicaoSchema),
  bloqueios: z.array(
    z.object({ motivo: z.enum(MOTIVOS_DE_BLOQUEIO_DE_ALOCACAO), detalhe: z.string() }),
  ),
})

export type CandidatoLido = z.infer<typeof candidatoSchema>

const candidatosSchema = z.object({
  reservaId: z.uuid(),
  kmPrevisto: z.number().int(),
  pesos: z.record(z.enum(FATORES_DE_ALOCACAO), z.number().int()),
  candidatos: z.array(candidatoSchema),
})

export type CandidatosLidos = z.infer<typeof candidatosSchema>

const alocacaoFeitaSchema = z.object({
  alocacaoId: z.uuid(),
  vehicleId: z.uuid(),
  placa: z.string(),
  modelo: z.string(),
  automatica: z.boolean(),
  score: z.number().int().nullable(),
})

// --- Contratos ----------------------------------------------------------------

const contratoSchema = z.object({
  id: z.uuid(),
  numero: z.number().int(),
  reservationId: z.uuid(),
  status: z.enum(STATUS_DE_CONTRATO),
  provedor: z.enum(PROVEDORES_DE_ASSINATURA).nullable(),
  hashDoDocumento: z.string(),
  envelopeExterno: z.string().nullable(),
  enviadoEm: z.string().nullable(),
  assinadoEm: z.string().nullable(),
  canceladoEm: z.string().nullable(),
  criadoEm: z.string(),
})

export type ContratoLido = z.infer<typeof contratoSchema>

const listaDeContratosSchema = z.object({ itens: z.array(contratoSchema) })
const downloadSchema = z.object({ url: z.string(), expiraEmSegundos: z.number().int() })

// ---------------------------------------------------------------------------
// Chaves de query
// ---------------------------------------------------------------------------

export const chavesDeReservas = {
  lista: (filtros: Readonly<Record<string, ValorDeQuery>>) =>
    ['reservas', 'lista', filtros] as const,
  detalhe: (id: string) => ['reservas', 'detalhe', id] as const,
  fila: () => ['reservas', 'fila'] as const,
  candidatos: (id: string) => ['reservas', 'candidatos', id] as const,
  contratos: (id: string) => ['reservas', 'contratos', id] as const,
  documentos: (id: string) => ['reservas', 'documentos', id] as const,
  multasDaReserva: (id: string) => ['reservas', 'multas', id] as const,
  sinistrosDaReserva: (id: string) => ['reservas', 'sinistros', id] as const,
}

/** Tudo que uma mudança de estado pode ter tornado velho, de uma vez. */
function invalidarReserva(queryClient: ReturnType<typeof useQueryClient>, id: string): void {
  void queryClient.invalidateQueries({ queryKey: ['reservas'] })
  void queryClient.invalidateQueries({ queryKey: chavesDeReservas.detalhe(id) })
}

// ---------------------------------------------------------------------------
// Leitura
// ---------------------------------------------------------------------------

export interface FiltrosDaLista {
  readonly pagina: number
  readonly status?: string | undefined
  readonly apenasVivas?: boolean | undefined
  readonly categoryId?: string | undefined
  readonly busca?: string | undefined
}

export function useReservas(filtros: FiltrosDaLista): UseQueryResult<ListaDeReservas> {
  const parametros: Record<string, ValorDeQuery> = {
    pagina: filtros.pagina,
    tamanho: 20,
    status: filtros.status,
    apenasVivas: filtros.apenasVivas === true ? 'true' : undefined,
    categoryId: filtros.categoryId,
    busca: filtros.busca === '' ? undefined : filtros.busca,
  }
  return useQuery({
    queryKey: chavesDeReservas.lista(parametros),
    queryFn: () =>
      apiGet(`${ROTAS_DE_RESERVAS.lista}${montarQuery(parametros)}`, listaDeReservasSchema),
  })
}

export function useReserva(id: string | null): UseQueryResult<ReservaDetalhe> {
  return useQuery({
    queryKey: chavesDeReservas.detalhe(id ?? 'nenhuma'),
    queryFn: () => apiGet(ROTAS_DE_RESERVAS.porId(id ?? ''), reservaDetalheSchema),
    enabled: id !== null,
  })
}

export function useFilaDeAprovacao(): UseQueryResult<{ itens: ItemDaFila[] }> {
  return useQuery({
    queryKey: chavesDeReservas.fila(),
    queryFn: () => apiGet(ROTAS_DE_RESERVAS.filaDeAprovacao, filaSchema),
    // A fila é a tela que o gestor deixa aberta: renovar sozinha a cada minuto
    // é o SLA aparecendo sem F5.
    refetchInterval: 60_000,
  })
}

export function useCandidatos(id: string | null): UseQueryResult<CandidatosLidos> {
  return useQuery({
    queryKey: chavesDeReservas.candidatos(id ?? 'nenhuma'),
    queryFn: () => apiGet(ROTAS_DE_ALOCACOES.candidatos(id ?? ''), candidatosSchema),
    enabled: id !== null,
  })
}

export function useContratos(id: string | null): UseQueryResult<{ itens: ContratoLido[] }> {
  return useQuery({
    queryKey: chavesDeReservas.contratos(id ?? 'nenhuma'),
    queryFn: () => apiGet(ROTAS_DE_CONTRATOS.daReserva(id ?? ''), listaDeContratosSchema),
    enabled: id !== null,
  })
}

export function useDocumentosDaReserva(
  id: string | null,
): UseQueryResult<{ itens: DocumentoDaReserva[] }> {
  return useQuery({
    queryKey: chavesDeReservas.documentos(id ?? 'nenhuma'),
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_DOCUMENTOS.daReserva(id ?? ''), listaDeDocumentosSchema, { signal }),
    enabled: id !== null,
  })
}

// --- O recorte que o FECHAMENTO escolhe: multas e sinistros da reserva ------
//
// Schemas mínimos locais (só o que o diálogo mostra), consultando as listagens
// com o filtro `reservationId`. As features de multas/sinistros têm as telas
// completas; o fechamento só precisa de descrição, valor e cobrabilidade.

const multaDaReservaSchema = z.object({
  id: z.uuid(),
  autoDeInfracao: z.string(),
  descricao: z.string(),
  valor: z.string(),
  status: z.enum(STATUS_DE_MULTA),
})

export type MultaDaReserva = z.infer<typeof multaDaReservaSchema>

const sinistroDaReservaSchema = z.object({
  id: z.uuid(),
  tipo: z.enum(TIPOS_DE_SINISTRO),
  status: z.enum(STATUS_DE_SINISTRO),
  descricao: z.string(),
  franquiaDaApolice: z.string().nullable(),
  responsabilidadeDoCliente: z.boolean(),
  cobradoEm: z.string().nullable(),
})

export type SinistroDaReserva = z.infer<typeof sinistroDaReservaSchema>

export function useMultasDaReserva(id: string | null): UseQueryResult<MultaDaReserva[]> {
  return useQuery({
    queryKey: chavesDeReservas.multasDaReserva(id ?? 'nenhuma'),
    queryFn: async ({ signal }) => {
      const query = montarQuery({ reservationId: id, pagina: 1, tamanho: 50 })
      const pagina = await apiGet(
        `${ROTAS_DE_MULTAS.lista}${query}`,
        paginaDe(multaDaReservaSchema),
        { signal },
      )
      return pagina.itens
    },
    enabled: id !== null,
  })
}

export function useSinistrosDaReserva(id: string | null): UseQueryResult<SinistroDaReserva[]> {
  return useQuery({
    queryKey: chavesDeReservas.sinistrosDaReserva(id ?? 'nenhuma'),
    queryFn: async ({ signal }) => {
      const query = montarQuery({ reservationId: id, pagina: 1, tamanho: 50 })
      const pagina = await apiGet(
        `${ROTAS_DE_SINISTROS.lista}${query}`,
        paginaDe(sinistroDaReservaSchema),
        { signal },
      )
      return pagina.itens
    },
    enabled: id !== null,
  })
}

// ---------------------------------------------------------------------------
// Escrita
// ---------------------------------------------------------------------------

export function useCriarReserva(): UseMutationResult<
  ReservaDetalhe,
  Error,
  { corpo: CriarReservaEntrada; chaveDeIdempotencia: string }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ corpo, chaveDeIdempotencia }) =>
      apiPost(ROTAS_DE_RESERVAS.lista, reservaDetalheSchema, corpo, {
        idempotencyKey: chaveDeIdempotencia,
      }),
    onSuccess: (reserva) => {
      invalidarReserva(queryClient, reserva.id)
    },
  })
}

export function useTransicionar(
  id: string,
): UseMutationResult<
  ReservaDetalhe,
  Error,
  { para: string; motivo?: string | undefined; motivoId?: string | undefined }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (corpo) => apiPost(ROTAS_DE_RESERVAS.transicoes(id), reservaDetalheSchema, corpo),
    onSuccess: () => {
      invalidarReserva(queryClient, id)
    },
    // 422 de máquina significa "o estado mudou embaixo de você": recarregar o
    // detalhe é parte do tratamento, não cortesia.
    onError: () => {
      invalidarReserva(queryClient, id)
    },
  })
}

export function useAprovar(id: string): UseMutationResult<ReservaDetalhe, Error, void> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiPost(ROTAS_DE_RESERVAS.aprovar(id), reservaDetalheSchema, {}),
    onSuccess: () => {
      invalidarReserva(queryClient, id)
    },
  })
}

export function useRecusar(
  id: string,
): UseMutationResult<ReservaDetalhe, Error, { motivo: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (corpo) =>
      apiPost(ROTAS_DE_RESERVAS.recusar(id), reservaDetalheSchema, {
        para: 'RECUSADA',
        motivo: corpo.motivo,
      }),
    onSuccess: () => {
      invalidarReserva(queryClient, id)
    },
  })
}

export function useProrrogar(
  id: string,
): UseMutationResult<ReservaDetalhe, Error, ProrrogarReservaEntrada> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (corpo) => apiPost(ROTAS_DE_RESERVAS.prorrogar(id), reservaDetalheSchema, corpo),
    onSuccess: () => {
      invalidarReserva(queryClient, id)
    },
  })
}

export function useSolicitarDocumento(
  id: string,
): UseMutationResult<unknown, Error, SolicitarDocumentoEntrada> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (corpo) => apiPost(ROTAS_DE_RESERVAS.solicitarDocumento(id), z.unknown(), corpo),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chavesDeReservas.documentos(id) })
      void queryClient.invalidateQueries({ queryKey: chavesDeReservas.fila() })
    },
  })
}

const uploadAssinadoSchema = z.object({
  urlDeUpload: z.string(),
  cabecalhos: z.record(z.string(), z.string()),
  chave: z.string(),
})

/**
 * Anexa um documento no balcão: assina o upload (`documento-de-cliente`), faz
 * o `PUT` cru direto no storage (o binário nunca toca a API; os cabeçalhos são
 * EXATAMENTE os assinados) e grava a chave no documento.
 */
export function useAnexarDocumento(
  reservaId: string,
): UseMutationResult<unknown, Error, { documentoId: string; arquivo: File }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ documentoId, arquivo }) => {
      const assinatura = await apiPost(ROTAS_DE_ARQUIVOS.documentoDeCliente, uploadAssinadoSchema, {
        tipoMime: arquivo.type,
        tamanhoEmBytes: arquivo.size,
      })
      const resposta = await fetch(assinatura.urlDeUpload, {
        method: 'PUT',
        headers: assinatura.cabecalhos,
        body: arquivo,
      }).catch(() => null)
      if (!resposta?.ok) {
        throw new Error('O armazenamento recusou o arquivo. Tente de novo.')
      }
      return apiPost(ROTAS_DE_DOCUMENTOS.anexar(reservaId, documentoId), z.unknown(), {
        arquivoChave: assinatura.chave,
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chavesDeReservas.documentos(reservaId) })
      void queryClient.invalidateQueries({ queryKey: chavesDeReservas.fila() })
    },
  })
}

export function useAvaliarDocumento(
  reservaId: string,
): UseMutationResult<unknown, Error, { documentoId: string } & AvaliarDocumento> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ documentoId, ...corpo }) =>
      apiPost(ROTAS_DE_DOCUMENTOS.avaliar(reservaId, documentoId), z.unknown(), corpo),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chavesDeReservas.documentos(reservaId) })
      void queryClient.invalidateQueries({ queryKey: chavesDeReservas.fila() })
    },
  })
}

export function useAlocar(
  id: string,
): UseMutationResult<z.infer<typeof alocacaoFeitaSchema>, Error, AlocarVeiculoEntrada> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (corpo) => apiPost(ROTAS_DE_ALOCACOES.alocar(id), alocacaoFeitaSchema, corpo),
    onSuccess: () => {
      invalidarReserva(queryClient, id)
      void queryClient.invalidateQueries({ queryKey: chavesDeReservas.candidatos(id) })
    },
  })
}

export function useDesfazerAlocacao(
  id: string,
): UseMutationResult<unknown, Error, { motivo: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (corpo) => apiPost(ROTAS_DE_ALOCACOES.desfazer(id), z.unknown(), corpo),
    onSuccess: () => {
      invalidarReserva(queryClient, id)
      void queryClient.invalidateQueries({ queryKey: chavesDeReservas.candidatos(id) })
    },
  })
}

export function useSimularFechamento(
  id: string,
): UseMutationResult<FechamentoLido, Error, FecharReservaEntrada> {
  return useMutation({
    mutationFn: (corpo) =>
      apiPost(ROTAS_DE_RESERVAS.simularFechamento(id), fechamentoSchema, corpo),
  })
}

export function useFechar(
  id: string,
): UseMutationResult<FechamentoLido, Error, FecharReservaEntrada> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (corpo) => apiPost(ROTAS_DE_RESERVAS.fechar(id), fechamentoSchema, corpo),
    onSuccess: () => {
      invalidarReserva(queryClient, id)
    },
  })
}

// --- Contratos ----------------------------------------------------------------

export function useGerarContrato(reservaId: string): UseMutationResult<ContratoLido, Error, void> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiPost(ROTAS_DE_CONTRATOS.daReserva(reservaId), contratoSchema, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chavesDeReservas.contratos(reservaId) })
    },
  })
}

export function useAcaoDeContrato(
  reservaId: string,
): UseMutationResult<
  ContratoLido,
  Error,
  { contratoId: string; acao: 'enviar' | 'assinar' | 'cancelar'; provedor?: string }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ contratoId, acao, provedor }) => {
      if (acao === 'enviar') {
        return apiPost(ROTAS_DE_CONTRATOS.enviarParaAssinatura(contratoId), contratoSchema, {
          provedor: provedor ?? 'MANUAL',
        })
      }
      if (acao === 'assinar') {
        return apiPost(ROTAS_DE_CONTRATOS.registrarAssinatura(contratoId), contratoSchema, {})
      }
      return apiPost(ROTAS_DE_CONTRATOS.cancelar(contratoId), contratoSchema, {})
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chavesDeReservas.contratos(reservaId) })
      invalidarReserva(queryClient, reservaId)
    },
  })
}

/** Pede a URL assinada e abre o PDF — o binário nunca passa pela API. */
export function useBaixarContrato(): UseMutationResult<void, Error, string> {
  return useMutation({
    mutationFn: async (contratoId) => {
      const { url } = await apiGet(ROTAS_DE_CONTRATOS.download(contratoId), downloadSchema)
      window.open(url, '_blank', 'noopener')
    },
  })
}
